import { Injectable, OnDestroy } from '@angular/core';
import { Socket, io } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ChannelState {
  clientIds: string[];
  licensesAssigned: Record<string, any> | null;
  lastHeartbeatTimestamp: number;
  userId: string;
  appName: string;
  appEnv: string;
  channelHash: string;
  licenseExpirationDate: string;
  error?: string;
}

export interface ChannelJoinRequest {
  userId: string;
  appName: string;
  features: string[];
  appEnv: string;
  licenseTag: string;
  checkoutTimeInMilliseconds?: number;
}

@Injectable({
  providedIn: 'root'
})
export class LicenseSocketService implements OnDestroy {
  private socket: Socket;
  private destroy$ = new Subject<void>();
  private channelState$ = new BehaviorSubject<ChannelState | null>(null);
  private connectionTimeout: any;

  constructor() {
    this.socket = io({
      path: "/license-server/socket.io",
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      timeout: 5000,
      transports: ['websocket', 'polling'],
    });

    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    this.socket.on('connect', () => {
      console.log('Connected to license server');
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.setErrorState('Error de conexión con el servidor de licencias');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from license server:', reason);
      if (reason === 'io server disconnect') {
        // Server initiated disconnect
        this.setErrorState('Servidor de licencias no disponible');
      }
      this.channelState$.next(null);
    });

    this.socket.on('channelStateUpdate', (state: ChannelState) => {
      this.channelState$.next(state);
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
      this.setErrorState('Error en el servidor de licencias');
    });
  }

  private setErrorState(errorMessage: string) {
    const currentState = this.channelState$.value;
    if (currentState) {
      this.channelState$.next({
        ...currentState,
        clientIds: [],
        licensesAssigned: null,
        error: errorMessage
      });
    }
  }

  public connect() {
    if (!this.socket.connected) {
      // Set a timeout for the connection attempt
      this.connectionTimeout = setTimeout(() => {
        if (!this.socket.connected) {
          this.setErrorState('Tiempo de espera agotado al conectar con el servidor de licencias');
        }
      }, 5000);
      
      this.socket.connect();
    }
  }

  public disconnect() {
    if (this.socket.connected) {
      this.socket.disconnect();
    }
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  public joinLicenseChannel(params: ChannelJoinRequest): Promise<any> {

    console.log('joinLicenseChannel', params);
    // Set initial state when requesting license
    const initialState: ChannelState = {
      clientIds: [params.userId],
      licensesAssigned: null,
      lastHeartbeatTimestamp: Date.now(),
      userId: params.userId,
      appName: params.appName,
      appEnv: params.appEnv,
      channelHash: '', // Will be set by server
      licenseExpirationDate: ''
    };
    this.channelState$.next(initialState);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.setErrorState('Tiempo de espera agotado al solicitar licencia');
        reject(new Error('Timeout waiting for license response'));
      }, 60000);

      this.socket.emit('joinLicenseChannel', params, (response: any) => {
        clearTimeout(timeout);
        if (response.success) {
          resolve(response);
        } else {
          this.setErrorState(response.message || 'Error al solicitar licencia');
          reject(new Error(response.message || 'Failed to join license channel'));
        }
      });
    });
  }

  public sendHeartbeat(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.setErrorState('Error en el heartbeat');
        reject(new Error('Heartbeat timeout'));
      }, 120000);

      this.socket.emit('heartbeat', (error: any) => {
        clearTimeout(timeout);
        if (error) {
          this.setErrorState('Error en el heartbeat');
          reject(error);
        } else {
          resolve();
        }
      });
    });
  }

  public getChannelState(): Observable<ChannelState | null> {
    return this.channelState$.asObservable();
  }

  public startHeartbeat(interval: number = 30000) {
    const heartbeatInterval = setInterval(() => {
      if (this.socket.connected) {
        this.sendHeartbeat().catch(error => {
          console.error('Heartbeat failed:', error);
        });
      }
    }, interval);

    // Clean up interval when service is destroyed
    this.destroy$.subscribe(() => {
      clearInterval(heartbeatInterval);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
} 